import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

import { EApprovalStatusValue } from '../../../approval/interfaces/approval.interface';
import {
  ISalesOrder,
  IOrderTrackingNumber,
  ESalesOrderSource,
} from '../../../order/interfaces/order.interface';
import { PicklistStatus } from '../../../picklist/interfaces/picklist.interface';
import { SalesOrderColorPickerConfig } from '../../configs/sales-order-color-picker.config';
import { ESalesOrderStatusValue } from '../../interfaces/sales-order.interface';

const EEdiTransactionNumberConstant = {
  TXN_810: '810',
  TXN_855: '855',
  TXN_856: '856',
};

interface ISalesOrderDropdownActionCellRendererParams extends ICellRendererParams {
  updateCustomFields :(data :ISalesOrder) => void;
  applyBackgroundColor :(data :{ id :number; backgroundColor :string }) => void;
  openCreatePicklistModal :(id :number) => void;
  deleteSalesOrder :(id :number) => void;
  editSalesOrder :(id :number) => void;
  flagOrder :(id :number) => void;
  unFlagOrder :(id :number) => void;
  approveOrder :(data :ISalesOrder) => void;
  rejectOrder :(data :ISalesOrder) => void;
  updateTags :(id :number) => void;
  onSendEdiTransactionDocument :(
    salesOrderId :number,
    ediTransactionNumber :string,
  ) => void;
  openCreateTrackingNumber :(data :{
    salesOrderId :number;
    trackingNumbers :string[];
  }) => void;
  setToPending :(id :number) => void;
  holdOrder :(data :{
    id :number;
    orderNumber :string;
    source :string;
    shipstationOrderId ?:any;
  }) => void;
  printShippingLabel :(data :any) => void;
  createSalesOrderInvoice :(shipstationOrderId :number) => void;
  pushFulfillment :(id :number) => void;
  ftPickAndPackEnabled :boolean;
  ftEdiIntegrationEnabled :boolean;
  ftApprovalModuleEnabled :boolean;
  ftEdi810InvoiceSyncEnabled :boolean;
  authUserId :number;
  canDeleteSalesOrder :boolean;
  onUnpostOrder :(id :number) => void;
}

@Component({
  selector: 'lum-sales-order-dropdown-action-cell-renderer',
  templateUrl: './sales-order-dropdown-action-cell-renderer.component.html',
  styleUrls: ['./sales-order-dropdown-action-cell-renderer.component.scss'],
})
export class SalesOrderDropdownActionCellRendererComponent
  implements ICellRendererAngularComp
{
  params :ISalesOrderDropdownActionCellRendererParams;

  ftPickAndPackEnabled :boolean = false;
  ftEdi810InvoiceSyncEnabled :boolean = false;
  ftEdiIntegrationEnabled :boolean = false;
  ftApprovalModuleEnabled :boolean = false;
  ediTransactionNumberConstant = EEdiTransactionNumberConstant;

  authUserId :number;

  canDeleteSalesOrder :boolean = false;

  selectedColor :string = '';
  colorPalette = SalesOrderColorPickerConfig.colorPalette;

  eSalesOrderSource = ESalesOrderSource;

  constructor(private router :Router) {}

  agInit(params :ISalesOrderDropdownActionCellRendererParams) :void {
    this.params = params;

    this.authUserId = this.params.authUserId;
    this.ftApprovalModuleEnabled = this.params.ftApprovalModuleEnabled;
    this.ftEdiIntegrationEnabled = this.params.ftEdiIntegrationEnabled;
    this.ftPickAndPackEnabled = this.params.ftPickAndPackEnabled;
    this.ftEdi810InvoiceSyncEnabled = this.params.ftEdi810InvoiceSyncEnabled;
    this.canDeleteSalesOrder = this.params.canDeleteSalesOrder;

    this.selectedColor = this.params.data?.backgroundColor || '';
  }

  updateCustomFields() :void {
    this.params.updateCustomFields(this.params.data);
  }

  applyBackgroundColor(color :string) :void {
    const selectedRowNode = this.params.node;
    selectedRowNode.data.backgroundColor = color;

    setTimeout(() => {
      this.params.api.redrawRows({ rowNodes: [selectedRowNode] });
      this.params.applyBackgroundColor({
        id: this.params.data.id,
        backgroundColor: color,
      });
    });
  }

  openCreatePicklistModal() :void {
    this.params.openCreatePicklistModal(this.params.data.id);
  }

  openCreateTrackingNumber() :void {
    this.params.openCreateTrackingNumber({
      salesOrderId: this.params.data.id,
      trackingNumbers:
        this.params.data?.trackingNumbers.map(
          (item :IOrderTrackingNumber) => item.trackingNumber,
        ) || [],
    });
  }

  editSalesOrder() :void {
    this.params.editSalesOrder(this.params.data.id);
  }

  deleteSalesOrder() :void {
    this.params.deleteSalesOrder(this.params.data.id);
  }

  createSalesOrderInvoice() :void {
    this.params.createSalesOrderInvoice(this.params.data.id);
  }

  flagOrder() :void {
    this.params.flagOrder(this.params.data.id);
  }

  unFlagOrder() :void {
    this.params.unFlagOrder(this.params.data.id);
  }

  setToPending() :void {
    this.params.setToPending(this.params.data.id);
  }

  holdOrder() :void {
    this.params.holdOrder({
      id: this.params.data.id,
      orderNumber: this.params.data.orderNumber,
      source: this.params.data.source,
      shipstationOrderId: this.params.data.shipstationOrderId,
    });
  }

  printShippingLabel() :void {
    this.params.printShippingLabel(this.params.data);
  }

  updateTags() :void {
    this.params.updateTags(this.params.data.id);
  }

  gotoViewSalesOrder() :void {
    this.router.navigate(['/shipping/posted-orders', this.params.data.id]);
  }

  gotoVisualPicking() {
    this.router.navigate([
      '/shipping/visual-picking/',
      this.params.data.picklist.id,
    ]);
  }

  gotoPackingPage() {
    this.router.navigate(['/shipping/packing/', this.params.data.picklist.id]);
  }

  refresh(params :ISalesOrderDropdownActionCellRendererParams) :boolean {
    return false;
  }

  approveOrder() :void {
    this.params.approveOrder(this.params.data);
  }

  rejectOrder() :void {
    this.params.rejectOrder(this.params.data);
  }

  onSendEdiTransactionDocument(ediTransactionNumber :string) :void {
    this.params.onSendEdiTransactionDocument(
      this.params.data.id,
      ediTransactionNumber,
    );
  }

  pushFulfillment() :void {
    this.params.pushFulfillment(this.params.data.id);
  }

  onUnpostOrder() :void {
    this.params.onUnpostOrder(this.params.data.id);
  }

  get canSendEdi810() :boolean {
    return this.canSendEdi;
  }

  get canSendEdi() :boolean {
    return this.params?.data?.source === ESalesOrderSource.EDI;
  }

  get showEditSalesOrderAction() :boolean {
    return this.params?.data?.orderStatus
      ? this.params.data.orderStatus !== ESalesOrderStatusValue.CANCELLED &&
          this.params.data.orderStatus !== ESalesOrderStatusValue.SHIPPED
      : false;
  }

  get showHoldOrderAction() :boolean {
    return this.params?.data?.orderStatus
      ? this.params.data.orderStatus !== ESalesOrderStatusValue.ON_HOLD &&
          this.params.data.orderStatus !== ESalesOrderStatusValue.SHIPPED
      : false;
  }

  get showSetToPendingButton() :boolean {
    return this.params?.data?.orderStatus
      ? this.params.data.orderStatus === ESalesOrderStatusValue.ON_HOLD
      : false;
  }

  get isPicking() :boolean {
    return this.params?.data?.picklist?.id > 0
      ? this.params.data.picklist.status === PicklistStatus.CREATED ||
          this.params.data.picklist.status === PicklistStatus.PICKING
      : false;
  }

  get isPacking() :boolean {
    return this.params?.data?.picklist?.id > 0
      ? this.params.data.picklist.status === PicklistStatus.PICKED ||
          this.params.data.picklist.status === PicklistStatus.PACKED
      : false;
  }

  get isPosted() :boolean {
    return !!this.params.data?.isPosted;
  }

  get shouldShowPrintShippingLabelButton() :boolean {
    return (
      this.params.data?.source === ESalesOrderSource.SHIPSTATION &&
      this.params.data?.isPosted &&
      this.params.data?.hasShippingLabel
    );
  }

  get canApproveOrReject() :boolean {
    return (
      this.ftApprovalModuleEnabled &&
      (this.params.data?.approval?.status === EApprovalStatusValue.PENDING ||
        this.params.data?.approval?.status === EApprovalStatusValue.PARTIAL) &&
      this.params.data?.approval?.approvalUsers.some(
        (user :any) =>
          user.userId === this.authUserId &&
          user.status === EApprovalStatusValue.PENDING,
      )
    );
  }
}
